import { BlueprintInstanceType, IslandType } from '@rta/rta-blueprint-library';
import type { Island, Kitchen } from '@rta/rta-blueprint-library';

import { GRANITE_FINISH } from './get-countertop-label';
import { getOverhangReductionCauses, type OverhangReductionCause } from './get-overhang-reduction-causes';

export type GetCountertopNotesParams = {
	island: Island;
	kitchen: Kitchen;
};

const STANDARD_OVERHANG = 2;
const STANDARD_SEATING_OVERHANG = 10;
const CORNER_TRIM_OVERHANG_VALUE = 0.75;
// Slat butts with a thinner corner trim than the other base finishes: .5" overhang removal (SOP), not .75".
const SLAT_CORNER_TRIM_OVERHANG_VALUE = 0.5;
const CORNER_TRIM_SUFFIX = ' to account for corner trim';
// Both an island trim-piece gap and an abutting obstruction trim the overhang by the same
// OVERHANG_REMOVAL_BY_MATERIAL (0.75"): physically it's the corner-trim thickness in either
// case, so a wall-abutted side reads the same as a trim-abutted side.
const OBSTRUCTION_SUFFIX = ' to account for corner trim';
const SAME_HEIGHT_OVERHANG = '-1';
const MOKS_SAME_HEIGHT_OVERHANG = '-.75';
const HEIGHT_TOLERANCE = 0.01;

const MOKS_STANDARD_OVERHANG = 0.75;
const MOKS_SEATING_OVERHANG = 8;

const SIDE_KEYS = ['front', 'left', 'back', 'right'] as const;

type SideDescriptor = { label: string; overhangKey: string };

// Ordered overhang sides per island. Corner islands have two back panels exposed
// as `leftBack` / `rightBack` (labelled "back left" / "back right", ordered back
// left then back right); all other islands use the single combined 'back'.
const getSideDescriptors = (island: Island): Array<SideDescriptor> => {
	if (island.type === IslandType.Corner) {
		return [
			{ label: 'front', overhangKey: 'front' },
			{ label: 'left', overhangKey: 'left' },
			{ label: 'back left', overhangKey: 'leftBack' },
			{ label: 'back right', overhangKey: 'rightBack' },
			{ label: 'right', overhangKey: 'right' }
		];
	}
	return SIDE_KEYS.map((side) => ({ label: side, overhangKey: side }));
};

// MOKS uses a different overhang system (SOP §9): .75" standard all sides, 8" seating, 0" padding.
const isMoksCountertop = (island: Island): boolean => {
	const key = island.getCountertop()?.material?.key ?? '';
	return key.toLowerCase().startsWith('mok');
};

// Slat is a base (surround) finish rather than a countertop material, so this reads island.surround (with a kitchen-default
// fallback) instead of island.getCountertop() -- same startsWith('slat') string-key idiom as isMoksCountertop, different lookup.
const isSlatFinish = (island: Island, kitchen: Kitchen): boolean => {
	const key = island.surround?.key ?? kitchen.material?.surround?.key ?? '';
	return key.toLowerCase().startsWith('slat');
};

const joinSides = (sides: Array<string>): string => {
	if (sides.length === 1) return sides[0];
	if (sides.length === 2) return `${sides[0]} and ${sides[1]}`;
	return `${sides.slice(0, -1).join(', ')}, and ${sides[sides.length - 1]}`;
};

// Formats "<value>" for the note: .75/.5 render without the leading zero to match the reference PDFs.
// The slat corner-trim value is only special-cased on a slat island -- a non-slat island with a
// genuinely custom 0.5" overhang (e.g. a wall-flush override, see finish-profiles.ts) must keep "0.5".
const formatOverhangValue = (value: number, isSlat: boolean): string => {
	if (value === CORNER_TRIM_OVERHANG_VALUE) return '.75';
	if (isSlat && value === SLAT_CORNER_TRIM_OVERHANG_VALUE) return '.5';
	return `${value}`;
};

/**
 * Sides where the countertop overhang was reduced to butt a same-height adjoining
 * island. Detected geometrically (a connected neighbor island of the same base height)
 * rather than by overhang value, which is unreliable (-1 on some designs, .75 on others).
 * Obstructions are skipped however tall they are — a wall is not a same-height island, and
 * its removal belongs to the wall overhang line instead.
 * Sides that keep the standard overhang (seating/front face) are excluded even when they butt.
 */
const getSameHeightSides = (island: Island, overhangs: Record<string, number>, standardOverhang: number): Array<string> => {
	const height = (island.size as { height?: number })?.height ?? 0;
	const connections = [...island.connections.getSourceConnections(), ...island.connections.getTargetConnections()];
	const descriptors = getSideDescriptors(island);

	const sides = connections
		.filter((connection) => {
			const { targetIsland } = connection;
			if (targetIsland?.instanceOf !== BlueprintInstanceType.Island) return false;
			const targetHeight = targetIsland && 'size' in targetIsland ? (targetIsland as { size?: { height?: number } }).size?.height ?? 0 : 0;
			return Math.abs(targetHeight - height) < HEIGHT_TOLERANCE;
		})
		.map((connection) => connection.sourcePanel?.label ?? '')
		.map((panelLabel) => descriptors.find((d) => d.label === panelLabel)?.label ?? panelLabel)
		.filter((label) => {
			if (label === '') return false;
			const { overhangKey } = descriptors.find((d) => d.label === label) ?? { overhangKey: label };
			return (overhangs[overhangKey] ?? standardOverhang) < standardOverhang;
		});

	return Array.from(new Set(sides));
};

/**
 * Names WHY a side's overhang is reduced instead of inferring it from the value: an obstruction cut
 * to the same corner-trim value a trim gap does, so the value alone used to mislabel walls as trim.
 * Every other side keeps its existing wording — the corner-trim value still reads as corner trim.
 */
const getOverhangSuffix = (value: number, standardOverhang: number, cause: OverhangReductionCause | undefined, cornerTrimValue: number): string => {
	if (cause === 'obstruction' && value < standardOverhang) return OBSTRUCTION_SUFFIX;
	return value === cornerTrimValue ? CORNER_TRIM_SUFFIX : '';
};

type OverhangLine = { value: number; suffix: string; sides: Array<string> };

/**
 * Builds one line per distinct overhang VALUE + cause, grouping every side that shares both onto a
 * single line, ordered front → left → back → right. Reduced sides name their cause via
 * `getOverhangSuffix`, so a wall-cut side no longer reads as corner trim.
 * Reads the real `island.getOverhangs()` value per side (no hardcoded MOKS
 * front/back/left/right assumptions), so only genuinely non-standard sides are
 * listed with their actual values. When `includeStandard` is true (no-RTA page)
 * every side is listed regardless of value. Same-height butting sides are excluded
 * (via `excludeSides`) so they are not double-listed alongside their dedicated note.
 */
const getOverhangValueLines = (island: Island, kitchen: Kitchen, includeStandard: boolean, excludeSides: Array<string>): Array<string> => {
	const overhangs = island.getOverhangs();
	const isMoks = isMoksCountertop(island);
	const isSlat = isSlatFinish(island, kitchen);
	const standardOverhang = isMoks ? MOKS_STANDARD_OVERHANG : STANDARD_OVERHANG;
	const seatingOverhang = isMoks ? MOKS_SEATING_OVERHANG : STANDARD_SEATING_OVERHANG;
	const cornerTrimValue = isSlat ? SLAT_CORNER_TRIM_OVERHANG_VALUE : CORNER_TRIM_OVERHANG_VALUE;
	const causes = getOverhangReductionCauses(island);

	const relevantSides = getSideDescriptors(island).filter(({ label, overhangKey }) => {
		if (excludeSides.includes(label)) return false;
		const value = overhangs[overhangKey] ?? standardOverhang;
		if (value < 0) return false;
		if (includeStandard) return true;
		return value !== standardOverhang && value !== seatingOverhang;
	});

	const linesByValueAndCause = new Map<string, OverhangLine>();
	relevantSides.forEach(({ label, overhangKey }) => {
		const value = overhangs[overhangKey] ?? standardOverhang;
		const suffix = getOverhangSuffix(value, standardOverhang, causes.get(label), cornerTrimValue);
		const line = linesByValueAndCause.get(`${value}${suffix}`) ?? { value, suffix, sides: [] };
		linesByValueAndCause.set(`${value}${suffix}`, { ...line, sides: [...line.sides, label] });
	});

	const lines: Array<string> = [];
	linesByValueAndCause.forEach(({ value, suffix, sides }) => {
		lines.push(`${formatOverhangValue(value, isSlat)}" ${joinSides(sides)} overhang${suffix}`);
	});
	return lines;
};

/**
 * Adjoining-island padding note. Emitted once per multi-island connected
 * countertop. Standard padding is 1" between butting islands. MOKS butts with
 * 0" padding (SOP §9A). Slat butts with .5" padding — thinner corner trim, not none.
 *
 * A "corner trim" join is one where at least one side of the connection is a SIDE
 * panel (side↔side, side↔front, side↔back) — those are the joins that physically
 * abut, so the trim strip sits in the gap. A galley connection is front↔front (or
 * back↔back / front↔back) with a walkway between the islands: no shared trim, so
 * no padding note. Handles pure galley kitchens and mixed kitchens (e.g. an L-shape
 * with a bar across from it) uniformly per-island.
 */
const getPaddingNote = (island: Island, kitchen: Kitchen): string | null => {
	const { islands } = kitchen.design;
	if (islands.children.length <= 1) return null;

	const hasCornerTrimConnection = [...island.connections.getSourceConnections(), ...island.connections.getTargetConnections()]
		.some((connection) => connection.sourcePanel?.isSidePanel || connection.targetPanel?.isSidePanel);
	if (!hasCornerTrimConnection) return null;

	if (isMoksCountertop(island)) return 'Please Note: 0" padding between islands';
	if (isSlatFinish(island, kitchen)) return 'Please Note: .5" padding between islands to account for corner trim';

	return 'Please Note: 1" padding between islands to account for corner trim';
};

// Emits the single "same height islands" note (ordered front → left → back → right)
// for the recessed butting sides, or null when there are none.
const getSameHeightNote = (island: Island, sameHeightSides: Array<string>): string | null => {
	const orderedSides = SIDE_KEYS.filter((side) => sameHeightSides.includes(side));
	if (orderedSides.length === 0) return null;
	const overhang = isMoksCountertop(island) ? MOKS_SAME_HEIGHT_OVERHANG : SAME_HEIGHT_OVERHANG;
	return `Please Note: ${overhang}" ${joinSides(orderedSides)} overhang to account same height islands`;
};

export const getCountertopNotes = (params: GetCountertopNotesParams): Array<string> => {
	const { island, kitchen } = params;
	const notes: Array<string> = [];

	const overhangs = island.getOverhangs();
	const standardOverhang = isMoksCountertop(island) ? MOKS_STANDARD_OVERHANG : STANDARD_OVERHANG;
	const sameHeightSides = getSameHeightSides(island, overhangs, standardOverhang);
	const sameHeightNote = getSameHeightNote(island, sameHeightSides);

	// No-RTA countertop (material "none"): reference-only page. Every side is
	// listed grouped by value; then the same-height note, then the padding note.
	const countertop = island.getCountertop();
	const materialKey = countertop?.material?.key ?? '';
	if (materialKey === 'none') {
		notes.push('Please Note: Countertop measurements per RTA specs for customer/contractor reference');
		notes.push(...getOverhangValueLines(island, kitchen, true, sameHeightSides).map((line) => `\t\t\t\t${line}`));
		if (sameHeightNote) notes.push(sameHeightNote);
		const paddingNote = getPaddingNote(island, kitchen);
		if (paddingNote) notes.push(paddingNote);
		return notes;
	}

	// Granite countertop pages lead with the material label and finish.
	if (kitchen.material?.countertop?.isGranite) {
		notes.push(`Granite | ${GRANITE_FINISH}`);
	}

	// Non-standard overhang lines (per value), then the same-height note, then the padding note.
	notes.push(...getOverhangValueLines(island, kitchen, false, sameHeightSides));
	if (sameHeightNote) notes.push(sameHeightNote);

	const paddingNote = getPaddingNote(island, kitchen);
	if (paddingNote) notes.push(paddingNote);

	return notes;
};
